import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { connect } from "react-redux";
import axios from "axios";
import { logout } from "../../store/actions/action";
import { useSearchModal } from "../../Context/SearchModalContext";
import { useCart } from "../../Context/CartContext";
import { totalPrice } from "../../utils";
import SearchModal from "../Modal/SearchModal";
import BrandLoader from "../BrandLoader/BrandLoader";

const buildInitials = (name = "", email = "") => {
  const src = name?.trim() || email?.split("@")?.[0] || "";
  const firstChar = src.replace(/[^A-Za-z0-9]/g, "")[0];
  return firstChar ? firstChar.toUpperCase() : "G";
};

const fmtMoney = (n) => Number(n || 0).toFixed(2);

const NAV_LINKS = [
  { to: "/shop", label: "Gift Cards" },
  { to: "/corporate-orders", label: "Business Orders" },
  { to: "/about", label: "About" },
  { to: "/support", label: "Support" },
  { to: "/contact", label: "Contact" },
];

const CURRENCIES = [
  { value: "AED", label: "AED", flag: "AE" },
  { value: "USD", label: "USD", flag: "US" },
];

const CURRENCY_SYMBOLS = { EUR: "€", USD: "$", AED: "AED" };

const Header = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams?.get("category");

  const { configs, carts = [] } = props;
  const { isAuth, token } = configs || {};
  const { cart } = useCart();
  const { openModal: openSearchModal } = useSearchModal();

  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutModal, setLogoutModal] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [currencySwitching, setCurrencySwitching] = useState(false);
  const [drawerCategoriesOpen, setDrawerCategoriesOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [passwordModal, setPasswordModal] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwForm, setPwForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [pwShow, setPwShow] = useState({ current: false, next: false, confirm: false });
  const [pwError, setPwError] = useState("");
  const [pwSuccess, setPwSuccess] = useState("");

  const [currency, setCurrency] = useState(
    typeof window !== "undefined"
      ? localStorage.getItem("currency") || "AED"
      : "AED"
  );

  const currencyRef = useRef(null);
  const categoryRef = useRef(null);
  const accountRef = useRef(null);

  // ----- ROUTE ACTIVE STATE
  const isActive = (path) => location?.pathname === path;
  const isConcierge =
    location?.pathname === "/concierge" || location?.pathname === "/redeem";

  // ----- SCROLL SHRINK
  useEffect(() => {
    const handleScroll = () =>
      setScrolled(document.documentElement.scrollTop > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ----- CLICK OUTSIDE TO CLOSE POPUPS
  useEffect(() => {
    const onDocClick = (e) => {
      if (currencyOpen && currencyRef.current && !currencyRef.current.contains(e.target)) {
        setCurrencyOpen(false);
      }
      if (categoryOpen && categoryRef.current && !categoryRef.current.contains(e.target)) {
        setCategoryOpen(false);
      }
      if (accountOpen && accountRef.current && !accountRef.current.contains(e.target)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [currencyOpen, categoryOpen, accountOpen]);

  // ----- FETCH PROFILE WHEN AUTHED
  useEffect(() => {
    let cancelled = false;
    if (isAuth && token) {
      axios
        .get("profile", { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          if (!cancelled && res?.data?.data) setUserProfile(res.data.data);
        })
        .catch(() => {
          if (!cancelled) setUserProfile(null);
        });
    } else {
      setUserProfile(null);
    }
    return () => {
      cancelled = true;
    };
  }, [isAuth, token]);

  // ----- BODY SCROLL LOCK WHEN DRAWER OPEN
  useEffect(() => {
    if (drawerOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  // ----- ESC KEY CLOSES DRAWER / POPUPS
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setDrawerOpen(false);
        setCurrencyOpen(false);
        setCategoryOpen(false);
        setAccountOpen(false);
        if (!pwLoading) setPasswordModal(false);
        if (!logoutLoading) setLogoutModal(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pwLoading, logoutLoading]);

  // ----- CLOSE DRAWER ON ROUTE CHANGE
  useEffect(() => {
    setDrawerOpen(false);
    setCategoryOpen(false);
    setAccountOpen(false);
  }, [location?.pathname, categoryParam]);

  // ----- LOCK BODY SCROLL WHILE A MODAL IS OPEN
  useEffect(() => {
    if (logoutModal || passwordModal) document.body.style.overflow = "hidden";
    else if (!drawerOpen) document.body.style.overflow = "";
  }, [logoutModal, passwordModal, drawerOpen]);

  // ----- HANDLERS
  const handleNav = (path) => {
    setDrawerOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCategoryPick = (categoryName) => {
    const sp = new URLSearchParams();
    sp.set("category", categoryName);
    setCategoryOpen(false);
    setDrawerOpen(false);
    setDrawerCategoriesOpen(false);
    navigate(`/shop?${sp.toString()}`);
  };

  const handleCurrencyChange = (next) => {
    if (next === currency) {
      setCurrencyOpen(false);
      return;
    }
    setCurrency(next);
    localStorage.setItem("currency", next);
    sessionStorage.setItem("ggp_currency_switching", "1");
    setCurrencyOpen(false);
    setCurrencySwitching(true);
    setTimeout(() => window.location.reload(), 350);
  };

  const handleSearchClick = () => {
    setDrawerOpen(false);
    openSearchModal();
  };

  const cartCount = carts?.length || 0;
  const cartTotalLabel = `${
    CURRENCY_SYMBOLS[currency] ? CURRENCY_SYMBOLS[currency] + " " : "AED "
  }${totalPrice(carts)}`;

  const websiteLogo = configs?.admin_conditions?.website_logo;
  const categories = (configs?.categories || []).filter(
    (c) => c?.name && c?.name?.toLowerCase() !== "all category"
  );
  const currentCurrencyOption =
    CURRENCIES.find((c) => c.value === currency) || CURRENCIES[0];

  const displayName = userProfile?.fullname || configs?.fullname || "";
  const displayEmail = userProfile?.email || configs?.email || "";
  const initials = buildInitials(displayName, displayEmail);
  const totalSpend = userProfile?.stats?.totalSpend;
  const totalOrders = userProfile?.stats?.totalOrders;

  const handleSignOut = () => {
    setLogoutLoading(true);
    setTimeout(() => {
      props?.logout();
      setLogoutLoading(false);
      setLogoutModal(false);
      setAccountOpen(false);
      setDrawerOpen(false);
      navigate("/");
    }, 250);
  };

  const goAndClose = (path) => {
    setAccountOpen(false);
    setDrawerOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const userHasPassword = userProfile?.hasPassword !== false;
  const userIsSocial = !!userProfile?.isSocialLogin;
  const userLoginType = userProfile?.loginType;

  const openPasswordModal = () => {
    setPwForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setPwShow({ current: false, next: false, confirm: false });
    setPwError("");
    setPwSuccess("");
    setAccountOpen(false);
    setDrawerOpen(false);
    setPasswordModal(true);
  };

  const closePasswordModal = () => {
    if (pwLoading) return;
    setPasswordModal(false);
  };

  const validatePassword = (pw) => {
    if (!pw || pw.length < 8) return "Password must be at least 8 characters.";
    if (!/[a-zA-Z]/.test(pw) || !/[0-9]/.test(pw)) return "Password must contain at least one letter and one number.";
    return "";
  };

  const submitPasswordChange = async (e) => {
    e?.preventDefault();
    setPwError("");
    setPwSuccess("");

    if (userHasPassword && !pwForm.currentPassword) {
      setPwError("Please enter your current password.");
      return;
    }
    const pwIssue = validatePassword(pwForm.newPassword);
    if (pwIssue) {
      setPwError(pwIssue);
      return;
    }
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwError("New password and confirmation do not match.");
      return;
    }

    setPwLoading(true);
    try {
      const payload = {
        newPassword: pwForm.newPassword,
        confirmPassword: pwForm.confirmPassword,
      };
      if (userHasPassword) payload.currentPassword = pwForm.currentPassword;

      const res = await axios.post("change-password", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPwSuccess(res?.data?.message || "Password updated successfully.");
      setPwForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      // refresh profile so loginType / hasPassword update
      try {
        const p = await axios.get("profile", { headers: { Authorization: `Bearer ${token}` } });
        if (p?.data?.data) setUserProfile(p.data.data);
      } catch {}
      setTimeout(() => setPasswordModal(false), 1400);
    } catch (err) {
      setPwError(err?.response?.data?.message || "Couldn't update password. Please try again.");
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <>
      {currencySwitching && (
        <BrandLoader
          label={`Switching to ${currency}`}
          sublabel="Updating prices across the store..."
        />
      )}

      <header className={`gpnv ${scrolled ? "gpnv-scrolled" : ""}`}>
        <div className="gpnv-inner">
          {/* LOGO */}
          <Link
            to="/"
            className="gpnv-brand"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            aria-label="GlobalGiftPass"
          >
            {websiteLogo ? (
              <img src={websiteLogo} alt="GlobalGiftPass" />
            ) : (
              <span className="gpnv-brand-text">
                <span>GLOBAL</span>GIFT PASS
              </span>
            )}
          </Link>

          {/* CATEGORY POPOVER (desktop only) */}
          <div className="gpnv-category" ref={categoryRef}>
            <button
              type="button"
              className={`gpnv-category-btn ${categoryOpen ? "is-open" : ""}`}
              onClick={() => setCategoryOpen((v) => !v)}
              aria-expanded={categoryOpen}
              aria-haspopup="true"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
              Browse
              <svg className={`gpnv-chev ${categoryOpen ? "gpnv-chev-up" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {categoryOpen && (
              <div className="gpnv-category-pop" role="menu">
                <div className="gpnv-category-head">
                  <span>Shop by Category</span>
                  <button
                    type="button"
                    className="gpnv-category-allbtn"
                    onClick={() => {
                      setCategoryOpen(false);
                      navigate("/shop");
                    }}
                  >
                    All →
                  </button>
                </div>
                <div className="gpnv-category-grid">
                  {categories.length === 0 && (
                    <div className="gpnv-category-empty">No categories yet.</div>
                  )}
                  {categories.map((cat) => {
                    const active = categoryParam === cat?.name;
                    return (
                      <button
                        key={cat?._id || cat?.name}
                        type="button"
                        className={`gpnv-category-item ${active ? "is-active" : ""}`}
                        onClick={() => handleCategoryPick(cat?.name)}
                      >
                        {cat?.url && (
                          <img
                            src={cat.url}
                            alt=""
                            className="gpnv-category-icon"
                          />
                        )}
                        <span>{cat?.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* PRIMARY NAV (desktop) */}
          <nav className="gpnv-links" aria-label="Primary">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`gpnv-link ${isActive(link.to) ? "gpnv-link-active" : ""}`}
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* ACTIONS */}
          <div className="gpnv-actions">
            {/* Search */}
            <button
              type="button"
              className="gpnv-icon-btn"
              onClick={handleSearchClick}
              aria-label="Search"
              title="Search products"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>

            {/* Currency */}
            <div className="gpnv-curr" ref={currencyRef}>
              <button
                type="button"
                className={`gpnv-curr-btn ${currencyOpen ? "is-open" : ""}`}
                onClick={() => setCurrencyOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={currencyOpen}
              >
                <img
                  src={`https://flagcdn.com/w40/${currentCurrencyOption.flag.toLowerCase()}.png`}
                  alt={currentCurrencyOption.label}
                  className="gpnv-curr-flag"
                />
                <span>{currentCurrencyOption.label}</span>
                <svg className={`gpnv-chev ${currencyOpen ? "gpnv-chev-up" : ""}`} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {currencyOpen && (
                <div className="gpnv-curr-pop" role="menu">
                  {CURRENCIES.map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      className={`gpnv-curr-opt ${c.value === currency ? "is-active" : ""}`}
                      onClick={() => handleCurrencyChange(c.value)}
                    >
                      <img
                        src={`https://flagcdn.com/w40/${c.flag.toLowerCase()}.png`}
                        alt={c.label}
                        className="gpnv-curr-flag"
                      />
                      {c.label}
                      {c.value === currency && (
                        <svg className="gpnv-curr-check" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Cart */}
            <Link to="/cart" className="gpnv-cart" aria-label={`Cart, ${cartCount} items`}>
              <span className="gpnv-cart-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1.5" />
                  <circle cx="19" cy="21" r="1.5" />
                  <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
                </svg>
                {cartCount > 0 && (
                  <span className="gpnv-cart-count">{cartCount}</span>
                )}
              </span>
              <span className="gpnv-cart-meta">
                <small>{cartCount} item{cartCount === 1 ? "" : "s"}</small>
                <strong>
                  {cart?.loading ? "Updating..." : cartTotalLabel}
                </strong>
              </span>
            </Link>

            {/* Account */}
            {isAuth && token ? (
              <div className="gpnv-account-wrap" ref={accountRef}>
                <button
                  type="button"
                  className={`gpnv-account ${accountOpen ? "is-open" : ""}`}
                  onClick={() => setAccountOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={accountOpen}
                >
                  <span className="gpnv-avatar gpnv-avatar-sm">
                    <span>{initials}</span>
                  </span>
                  <span className="gpnv-account-meta">
                    <small>Hello,</small>
                    <strong>
                      {displayName ? displayName.split(" ")[0] : "Account"}
                    </strong>
                  </span>
                  <svg className={`gpnv-chev gpnv-account-chev ${accountOpen ? "gpnv-chev-up" : ""}`} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {accountOpen && (
                  <div className="gpnv-account-pop" role="menu">
                    <div className="gpnv-account-pop-head">
                      <span className="gpnv-avatar gpnv-avatar-lg">
                        <span>{initials}</span>
                      </span>
                      <div className="gpnv-account-pop-id">
                        <strong>{displayName || "GlobalGiftPass Member"}</strong>
                        <small>{displayEmail || "—"}</small>
                      </div>
                    </div>

                    {userProfile?.stats && (
                      <div className="gpnv-account-pop-stats">
                        <div className="gpnv-account-stat">
                          <small>Orders</small>
                          <strong>{totalOrders || 0}</strong>
                        </div>
                        <div className="gpnv-account-stat">
                          <small>Spent</small>
                          <strong>AED {fmtMoney(totalSpend)}</strong>
                        </div>
                        <div className="gpnv-account-stat">
                          <small>Delivered</small>
                          <strong>{userProfile?.stats?.deliveredCount || 0}</strong>
                        </div>
                      </div>
                    )}

                    <div className="gpnv-account-pop-list">
                      <button
                        type="button"
                        className="gpnv-account-pop-link"
                        onClick={() => goAndClose("/your-orders")}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 2l1 6h10l1-6" />
                          <path d="M5 6h14v15a1 1 0 01-1 1H6a1 1 0 01-1-1V6z" />
                        </svg>
                        My Orders
                        <span className="gpnv-account-pop-arrow">→</span>
                      </button>
                      <button
                        type="button"
                        className="gpnv-account-pop-link"
                        onClick={() => goAndClose("/cart")}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="9" cy="21" r="1" />
                          <circle cx="20" cy="21" r="1" />
                          <path d="M1 1h4l2.7 13.4a2 2 0 002 1.6h9.7a2 2 0 002-1.6L23 6H6" />
                        </svg>
                        Cart
                        <span className="gpnv-account-pop-arrow">→</span>
                      </button>
                      <button
                        type="button"
                        className="gpnv-account-pop-link"
                        onClick={() => goAndClose("/redeem")}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3 17h18l-1.4-9.5a.5.5 0 0 0-.79-.34L15 10 12.4 4.7a.5.5 0 0 0-.9 0L9 10 4.19 7.16a.5.5 0 0 0-.79.34L2 17h1zm0 2h18v2H3z" />
                        </svg>
                        Redeem Voucher
                        <span className="gpnv-account-pop-arrow">→</span>
                      </button>

                      <button
                        type="button"
                        className="gpnv-account-pop-link"
                        onClick={openPasswordModal}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                        </svg>
                        {userHasPassword ? "Change Password" : "Set Password"}
                        {userIsSocial && (
                          <span className="gpnv-account-pop-badge">
                            {userLoginType === "google" ? "Google" : "Social"}
                          </span>
                        )}
                        <span className="gpnv-account-pop-arrow">→</span>
                      </button>
                    </div>

                    <div className="gpnv-account-pop-divider" />

                    <button
                      type="button"
                      className="gpnv-account-pop-signout"
                      onClick={() => {
                        setAccountOpen(false);
                        setLogoutModal(true);
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="gpnv-account">
                <span className="gpnv-account-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
                <span className="gpnv-account-meta">
                  <small>Hello,</small>
                  <strong>Sign in</strong>
                </span>
              </Link>
            )}

            {/* Concierge / Redeem */}
            <Link
              to="/redeem"
              className={`gpnv-concierge ${isConcierge ? "is-active" : ""}`}
              title="Concierge / Redeem your voucher"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M3 17h18l-1.4-9.5a.5.5 0 0 0-.79-.34L15 10 12.4 4.7a.5.5 0 0 0-.9 0L9 10 4.19 7.16a.5.5 0 0 0-.79.34L2 17h1zm0 2h18v2H3z" />
              </svg>
              <span className="gpnv-concierge-label">Concierge / Redeem</span>
              <span className="gpnv-concierge-short">Redeem</span>
            </Link>

            {/* Mobile hamburger */}
            <button
              type="button"
              className="gpnv-burger"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
      </header>

      {/* Spacer so page content is not hidden behind fixed header */}
      <div className={`gpnv-spacer ${scrolled ? "gpnv-spacer-scrolled" : ""}`} />

      {/* MOBILE DRAWER */}
      {drawerOpen && (
        <div className="gpnv-drawer-backdrop" onClick={() => setDrawerOpen(false)}>
          <aside
            className="gpnv-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="gpnv-drawer-head">
              <Link to="/" onClick={() => setDrawerOpen(false)} className="gpnv-drawer-brand">
                {websiteLogo ? (
                  <img src={websiteLogo} alt="GlobalGiftPass" />
                ) : (
                  <span className="gpnv-brand-text"><span>GLOBAL</span>GIFT PASS</span>
                )}
              </Link>
              <button
                type="button"
                className="gpnv-drawer-close"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <button
              type="button"
              className="gpnv-drawer-search"
              onClick={handleSearchClick}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              Search gift cards, brands...
            </button>

            <div className="gpnv-drawer-section">
              <div className="gpnv-drawer-label">Menu</div>
              <ul className="gpnv-drawer-list">
                <li>
                  <button
                    type="button"
                    className={`gpnv-drawer-link ${isActive("/") ? "is-active" : ""}`}
                    onClick={() => handleNav("/")}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9.5L12 3l9 6.5V20a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2V9.5z" /></svg>
                    Home
                  </button>
                </li>
                {NAV_LINKS.map((link) => (
                  <li key={link.to}>
                    <button
                      type="button"
                      className={`gpnv-drawer-link ${isActive(link.to) ? "is-active" : ""}`}
                      onClick={() => handleNav(link.to)}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
                {/* Categories collapsible */}
                <li className="gpnv-drawer-collapse">
                  <button
                    type="button"
                    className="gpnv-drawer-link gpnv-drawer-collapse-head"
                    onClick={() => setDrawerCategoriesOpen((v) => !v)}
                    aria-expanded={drawerCategoriesOpen}
                  >
                    <span>Browse Categories</span>
                    <svg className={`gpnv-chev ${drawerCategoriesOpen ? "gpnv-chev-up" : ""}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {drawerCategoriesOpen && (
                    <ul className="gpnv-drawer-sublist">
                      <li>
                        <button
                          type="button"
                          className="gpnv-drawer-sublink"
                          onClick={() => handleNav("/shop")}
                        >
                          All Gift Cards
                        </button>
                      </li>
                      {categories.map((cat) => (
                        <li key={cat?._id || cat?.name}>
                          <button
                            type="button"
                            className="gpnv-drawer-sublink"
                            onClick={() => handleCategoryPick(cat?.name)}
                          >
                            {cat?.url && (
                              <img src={cat.url} alt="" className="gpnv-drawer-subicon" />
                            )}
                            {cat?.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
                <li>
                  <button
                    type="button"
                    className={`gpnv-drawer-link ${isAuth && token ? "" : ""}`}
                    onClick={() => handleNav("/your-orders")}
                  >
                    Your Orders
                  </button>
                </li>
              </ul>
            </div>

            <div className="gpnv-drawer-section">
              <div className="gpnv-drawer-label">Currency</div>
              <div className="gpnv-drawer-currency">
                {CURRENCIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    className={`gpnv-drawer-currbtn ${c.value === currency ? "is-active" : ""}`}
                    onClick={() => handleCurrencyChange(c.value)}
                  >
                    <img
                      src={`https://flagcdn.com/w40/${c.flag.toLowerCase()}.png`}
                      alt={c.label}
                    />
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <Link
              to="/redeem"
              className="gpnv-drawer-concierge"
              onClick={() => setDrawerOpen(false)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 17h18l-1.4-9.5a.5.5 0 0 0-.79-.34L15 10 12.4 4.7a.5.5 0 0 0-.9 0L9 10 4.19 7.16a.5.5 0 0 0-.79.34L2 17h1zm0 2h18v2H3z" />
              </svg>
              Concierge / Redeem
            </Link>

            <div className="gpnv-drawer-account">
              {isAuth && token ? (
                <>
                  <div className="gpnv-drawer-user">
                    <span className="gpnv-avatar gpnv-avatar-md">
                      <span>{initials}</span>
                    </span>
                    <div className="gpnv-drawer-user-meta">
                      <strong>{displayName || "Member"}</strong>
                      <small>{displayEmail || "—"}</small>
                      {userProfile?.stats && (
                        <span className="gpnv-drawer-user-stats">
                          {totalOrders || 0} orders · AED {fmtMoney(totalSpend)} spent
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="gpnv-drawer-signbtn"
                    onClick={openPasswordModal}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    {userHasPassword ? "Change Password" : "Set Password"}
                    {userIsSocial && <small>via {userLoginType === "google" ? "Google" : "Social"}</small>}
                  </button>
                  <button
                    type="button"
                    className="gpnv-drawer-signbtn gpnv-drawer-signout"
                    onClick={() => {
                      setDrawerOpen(false);
                      setLogoutModal(true);
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
                    Sign Out
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="gpnv-drawer-signbtn"
                  onClick={() => handleNav("/login")}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" /></svg>
                  Sign in to your account
                </button>
              )}
            </div>

            {(configs?.admin_conditions?.email_support ||
              configs?.admin_conditions?.mobile_support) && (
              <div className="gpnv-drawer-contact">
                {configs?.admin_conditions?.mobile_support && (
                  <a href={`tel:${configs.admin_conditions.mobile_support}`}>
                    📞 {configs.admin_conditions.mobile_support}
                  </a>
                )}
                {configs?.admin_conditions?.email_support && (
                  <a href={`mailto:${configs.admin_conditions.email_support}`}>
                    ✉️ {configs.admin_conditions.email_support}
                  </a>
                )}
              </div>
            )}
          </aside>
        </div>
      )}

      {/* SEARCH MODAL */}
      <SearchModal />

      {/* PASSWORD MODAL */}
      {passwordModal && (
        <div
          className="gpnv-pw-backdrop"
          onClick={closePasswordModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="gpnv-pw-title"
        >
          <div className="gpnv-pw-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="gpnv-logout-close"
              onClick={closePasswordModal}
              disabled={pwLoading}
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="gpnv-pw-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>

            <h2 id="gpnv-pw-title" className="gpnv-pw-title">
              {userHasPassword ? (
                <>Change your <span>password</span></>
              ) : (
                <>Set a <span>password</span></>
              )}
            </h2>
            <p className="gpnv-pw-sub">
              {userHasPassword
                ? "Pick a strong password — at least 8 characters with letters and numbers."
                : `You signed in with ${userLoginType === "google" ? "Google" : "a social account"}. Set a password to also sign in with your email.`}
            </p>

            {userIsSocial && !userHasPassword && (
              <div className="gpnv-pw-social-badge">
                {userLoginType === "google" ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.1V7.07H2.18A10.99 10.99 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l3.66-2.83z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A10.99 10.99 0 0 0 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="10" /></svg>
                )}
                Linked with {userLoginType === "google" ? "Google" : "Social"}
                <span className="gpnv-pw-social-email">{displayEmail || ""}</span>
              </div>
            )}

            <form className="gpnv-pw-form" onSubmit={submitPasswordChange}>
              {userHasPassword && (
                <div className="gpnv-pw-field">
                  <label htmlFor="gpnv-pw-current">Current Password</label>
                  <div className="gpnv-pw-input-wrap">
                    <input
                      id="gpnv-pw-current"
                      type={pwShow.current ? "text" : "password"}
                      value={pwForm.currentPassword}
                      onChange={(e) => setPwForm((p) => ({ ...p, currentPassword: e.target.value }))}
                      disabled={pwLoading}
                      autoComplete="current-password"
                      placeholder="Enter your current password"
                    />
                    <button
                      type="button"
                      className="gpnv-pw-toggle"
                      onClick={() => setPwShow((s) => ({ ...s, current: !s.current }))}
                      aria-label={pwShow.current ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        {pwShow.current ? (
                          <>
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </>
                        ) : (
                          <>
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </>
                        )}
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              <div className="gpnv-pw-field">
                <label htmlFor="gpnv-pw-new">New Password</label>
                <div className="gpnv-pw-input-wrap">
                  <input
                    id="gpnv-pw-new"
                    type={pwShow.next ? "text" : "password"}
                    value={pwForm.newPassword}
                    onChange={(e) => setPwForm((p) => ({ ...p, newPassword: e.target.value }))}
                    disabled={pwLoading}
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                  />
                  <button
                    type="button"
                    className="gpnv-pw-toggle"
                    onClick={() => setPwShow((s) => ({ ...s, next: !s.next }))}
                    aria-label={pwShow.next ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {pwShow.next ? (
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </>
                      ) : (
                        <>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
                {pwForm.newPassword && (() => {
                  const pw = pwForm.newPassword;
                  const len = pw.length >= 8;
                  const hasLetter = /[a-zA-Z]/.test(pw);
                  const hasNum = /[0-9]/.test(pw);
                  const hasSym = /[^a-zA-Z0-9]/.test(pw);
                  const score = [len, hasLetter, hasNum, hasSym].filter(Boolean).length;
                  const label = ["Too weak", "Weak", "Fair", "Strong", "Excellent"][score] || "";
                  return (
                    <div className="gpnv-pw-strength">
                      <div className={`gpnv-pw-strength-bar gpnv-pw-strength-${score}`}>
                        <span /><span /><span /><span />
                      </div>
                      <span className="gpnv-pw-strength-label">{label}</span>
                    </div>
                  );
                })()}
              </div>

              <div className="gpnv-pw-field">
                <label htmlFor="gpnv-pw-confirm">Confirm New Password</label>
                <div className="gpnv-pw-input-wrap">
                  <input
                    id="gpnv-pw-confirm"
                    type={pwShow.confirm ? "text" : "password"}
                    value={pwForm.confirmPassword}
                    onChange={(e) => setPwForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                    disabled={pwLoading}
                    autoComplete="new-password"
                    placeholder="Re-enter the new password"
                  />
                  <button
                    type="button"
                    className="gpnv-pw-toggle"
                    onClick={() => setPwShow((s) => ({ ...s, confirm: !s.confirm }))}
                    aria-label={pwShow.confirm ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {pwShow.confirm ? (
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </>
                      ) : (
                        <>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
              </div>

              {pwError && (
                <div className="gpnv-pw-alert gpnv-pw-alert-error">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L1 21h22L12 2zm0 6l8 14H4l8-14zm-1 5h2v4h-2v-4zm0 5h2v2h-2v-2z" /></svg>
                  {pwError}
                </div>
              )}
              {pwSuccess && (
                <div className="gpnv-pw-alert gpnv-pw-alert-success">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
                  {pwSuccess}
                </div>
              )}

              <div className="gpnv-pw-actions">
                <button
                  type="button"
                  className="gpnv-logout-btn gpnv-logout-btn-cancel"
                  onClick={closePasswordModal}
                  disabled={pwLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="gpnv-pw-submit"
                  disabled={pwLoading}
                >
                  {pwLoading ? (
                    <span className="gpnv-logout-spinner" />
                  ) : (
                    <>
                      {userHasPassword ? "Update Password" : "Set Password"}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* LOGOUT MODAL */}
      {logoutModal && (
        <div
          className="gpnv-logout-backdrop"
          onClick={() => !logoutLoading && setLogoutModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="gpnv-logout-title"
        >
          <div
            className="gpnv-logout-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="gpnv-logout-close"
              onClick={() => setLogoutModal(false)}
              disabled={logoutLoading}
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <div className="gpnv-logout-icon">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </div>

            <h2 id="gpnv-logout-title" className="gpnv-logout-title">
              Sign out of your <span>account?</span>
            </h2>
            <p className="gpnv-logout-sub">
              {displayName
                ? `You're signed in as ${displayName}. Your cart will be preserved.`
                : "Your cart will be preserved for next time."}
            </p>

            {(displayName || displayEmail) && (
              <div className="gpnv-logout-user">
                <span className="gpnv-avatar gpnv-avatar-md">
                  <span>{initials}</span>
                </span>
                <div className="gpnv-logout-user-meta">
                  <strong>{displayName || "Member"}</strong>
                  <small>{displayEmail || ""}</small>
                </div>
              </div>
            )}

            <div className="gpnv-logout-actions">
              <button
                type="button"
                className="gpnv-logout-btn gpnv-logout-btn-cancel"
                onClick={() => setLogoutModal(false)}
                disabled={logoutLoading}
              >
                Stay Signed In
              </button>
              <button
                type="button"
                className="gpnv-logout-btn gpnv-logout-btn-confirm"
                onClick={handleSignOut}
                disabled={logoutLoading}
              >
                {logoutLoading ? (
                  <span className="gpnv-logout-spinner" />
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Sign Out
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* ============ NAVBAR ============ */
        .gpnv {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          background: linear-gradient(180deg, rgba(11, 11, 12, 0.96), rgba(15, 15, 16, 0.96));
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(212, 168, 87, 0.15);
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          transition: background .25s ease, border-color .25s ease, box-shadow .25s ease;
        }
        .gpnv-scrolled {
          background: rgba(11, 11, 12, 0.98);
          border-bottom-color: rgba(212, 168, 87, 0.3);
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.5);
        }
        .gpnv-spacer { height: 78px; }
        .gpnv-spacer-scrolled { height: 68px; }

        .gpnv-inner {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 22px;
          height: 78px;
          display: flex;
          align-items: center;
          gap: 16px;
          transition: height .25s ease;
        }
        .gpnv-scrolled .gpnv-inner { height: 68px; }

        /* BRAND */
        .gpnv-brand {
          display: flex;
          align-items: center;
          flex-shrink: 0;
          margin-right: 8px;
        }
        .gpnv-brand img {
          height: 56px;
          width: auto;
          object-fit: contain;
          transition: height .25s ease;
        }
        .gpnv-scrolled .gpnv-brand img { height: 48px; }
        .gpnv-brand-text {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-weight: 700;
          font-size: 16px;
          color: #d4a857;
          letter-spacing: 1.5px;
        }
        .gpnv-brand-text span {
          display: block;
          font-size: 11px;
          letter-spacing: 4px;
          color: #fff;
        }

        /* CATEGORY */
        .gpnv-category { position: relative; flex-shrink: 0; }
        .gpnv-category-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(212, 168, 87, 0.08);
          border: 1px solid rgba(212, 168, 87, 0.25);
          color: #fff;
          padding: 9px 14px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.3px;
          cursor: pointer;
          transition: background .2s ease, border-color .2s ease;
          font-family: inherit;
        }
        .gpnv-category-btn svg { color: #d4a857; }
        .gpnv-category-btn:hover,
        .gpnv-category-btn.is-open {
          background: rgba(212, 168, 87, 0.18);
          border-color: rgba(212, 168, 87, 0.5);
        }
        .gpnv-chev { transition: transform .25s ease; color: #d4a857; }
        .gpnv-chev-up { transform: rotate(180deg); }

        .gpnv-category-pop {
          position: absolute;
          top: calc(100% + 12px);
          left: 0;
          width: min(560px, 84vw);
          background: #15110a;
          border: 1px solid rgba(212, 168, 87, 0.3);
          border-radius: 14px;
          padding: 14px;
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.6);
          z-index: 1100;
          animation: gpnv-fade .18s ease;
        }
        @keyframes gpnv-fade {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .gpnv-category-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 6px 10px;
          margin-bottom: 8px;
          border-bottom: 1px solid rgba(212, 168, 87, 0.15);
          font-size: 11px;
          color: #d4a857;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }
        .gpnv-category-allbtn {
          background: transparent;
          border: none;
          color: #d4a857;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 1.2px;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 6px;
          font-family: inherit;
        }
        .gpnv-category-allbtn:hover { background: rgba(212, 168, 87, 0.12); }
        .gpnv-category-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 4px;
          max-height: 360px;
          overflow-y: auto;
        }
        .gpnv-category-grid::-webkit-scrollbar { width: 5px; }
        .gpnv-category-grid::-webkit-scrollbar-thumb { background: rgba(212, 168, 87, 0.25); border-radius: 999px; }
        .gpnv-category-empty { color: #6b7280; font-size: 13px; padding: 12px; }
        .gpnv-category-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 10px;
          background: transparent;
          border: 1px solid transparent;
          border-radius: 8px;
          color: #cfd2d6;
          font-size: 13px;
          text-align: left;
          cursor: pointer;
          transition: background .15s ease, color .15s ease, border-color .15s ease;
          font-family: inherit;
        }
        .gpnv-category-item:hover {
          background: rgba(212, 168, 87, 0.08);
          color: #fff;
          border-color: rgba(212, 168, 87, 0.18);
        }
        .gpnv-category-item.is-active {
          background: linear-gradient(90deg, rgba(212, 168, 87, 0.18), rgba(212, 168, 87, 0.05));
          color: #d4a857;
          border-color: rgba(212, 168, 87, 0.35);
          font-weight: 600;
        }
        .gpnv-category-icon {
          width: 22px;
          height: 22px;
          border-radius: 5px;
          object-fit: cover;
          background: rgba(255, 255, 255, 0.05);
          flex-shrink: 0;
        }

        /* PRIMARY NAV */
        .gpnv-links {
          display: flex;
          align-items: center;
          gap: 4px;
          flex: 1;
          justify-content: center;
        }
        .gpnv-link {
          color: #cfd2d6;
          font-size: 13.5px;
          font-weight: 500;
          padding: 8px 12px;
          border-radius: 8px;
          text-decoration: none;
          letter-spacing: 0.3px;
          position: relative;
          transition: color .2s ease, background .2s ease;
          white-space: nowrap;
        }
        .gpnv-link::after {
          content: "";
          position: absolute;
          left: 12px;
          right: 12px;
          bottom: 4px;
          height: 2px;
          background: linear-gradient(90deg, #d4a857, #b58a3f);
          border-radius: 2px;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform .25s ease;
        }
        .gpnv-link:hover {
          color: #fff;
          text-decoration: none;
        }
        .gpnv-link:hover::after { transform: scaleX(0.6); }
        .gpnv-link-active {
          color: #d4a857 !important;
        }
        .gpnv-link-active::after { transform: scaleX(1); }

        /* ACTIONS */
        .gpnv-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }
        .gpnv-icon-btn {
          width: 38px;
          height: 38px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(212, 168, 87, 0.18);
          border-radius: 10px;
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background .2s ease, border-color .2s ease, color .2s ease;
        }
        .gpnv-icon-btn:hover {
          background: rgba(212, 168, 87, 0.18);
          border-color: rgba(212, 168, 87, 0.5);
          color: #d4a857;
        }

        /* CURRENCY */
        .gpnv-curr { position: relative; }
        .gpnv-curr-btn {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(212, 168, 87, 0.18);
          color: #fff;
          padding: 7px 12px;
          border-radius: 10px;
          font-size: 12.5px;
          font-weight: 600;
          cursor: pointer;
          height: 38px;
          transition: background .2s ease, border-color .2s ease;
          font-family: inherit;
        }
        .gpnv-curr-btn:hover, .gpnv-curr-btn.is-open {
          background: rgba(212, 168, 87, 0.18);
          border-color: rgba(212, 168, 87, 0.5);
        }
        .gpnv-curr-flag {
          width: 18px;
          height: 12px;
          object-fit: cover;
          border-radius: 2px;
          flex-shrink: 0;
        }
        .gpnv-curr-pop {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          background: #15110a;
          border: 1px solid rgba(212, 168, 87, 0.3);
          border-radius: 10px;
          padding: 6px;
          min-width: 130px;
          box-shadow: 0 12px 28px rgba(0, 0, 0, 0.5);
          animation: gpnv-fade .18s ease;
          z-index: 1100;
        }
        .gpnv-curr-opt {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 10px;
          background: transparent;
          border: none;
          color: #cfd2d6;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          border-radius: 6px;
          font-family: inherit;
          text-align: left;
        }
        .gpnv-curr-opt:hover { background: rgba(212, 168, 87, 0.1); color: #fff; }
        .gpnv-curr-opt.is-active { color: #d4a857; font-weight: 700; }
        .gpnv-curr-check { color: #d4a857; margin-left: auto; }

        /* CART */
        .gpnv-cart {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          padding: 7px 14px 7px 10px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(212, 168, 87, 0.18);
          border-radius: 10px;
          color: #fff;
          text-decoration: none;
          height: 38px;
          transition: background .2s ease, border-color .2s ease;
        }
        .gpnv-cart:hover {
          color: #fff;
          text-decoration: none;
          background: rgba(212, 168, 87, 0.18);
          border-color: rgba(212, 168, 87, 0.5);
        }
        .gpnv-cart-icon {
          position: relative;
          color: #d4a857;
          display: flex;
          align-items: center;
        }
        .gpnv-cart-count {
          position: absolute;
          top: -8px;
          right: -10px;
          min-width: 18px;
          height: 18px;
          padding: 0 5px;
          background: linear-gradient(135deg, #d4a857, #b58a3f);
          color: #1a1208;
          font-size: 10px;
          font-weight: 700;
          border-radius: 999px;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
          border: 2px solid #15110a;
        }
        .gpnv-cart-meta {
          display: flex;
          flex-direction: column;
          line-height: 1.1;
        }
        .gpnv-cart-meta small {
          font-size: 10px;
          color: #9aa0a8;
          letter-spacing: 0.5px;
        }
        .gpnv-cart-meta strong {
          font-size: 13px;
          color: #d4a857;
          font-weight: 700;
        }

        /* ACCOUNT */
        .gpnv-account {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          padding: 7px 14px 7px 10px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(212, 168, 87, 0.18);
          border-radius: 10px;
          color: #fff;
          text-decoration: none;
          height: 38px;
          cursor: pointer;
          font-family: inherit;
          transition: background .2s ease, border-color .2s ease;
        }
        .gpnv-account:hover {
          background: rgba(212, 168, 87, 0.18);
          border-color: rgba(212, 168, 87, 0.5);
          color: #fff;
          text-decoration: none;
        }
        .gpnv-account-icon { color: #d4a857; display: flex; align-items: center; }
        .gpnv-account-meta {
          display: flex;
          flex-direction: column;
          line-height: 1.1;
          max-width: 110px;
          overflow: hidden;
        }
        .gpnv-account-meta small {
          font-size: 10px;
          color: #9aa0a8;
          letter-spacing: 0.5px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .gpnv-account-meta strong {
          font-size: 13px;
          font-weight: 700;
          color: #fff;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* CONCIERGE */
        .gpnv-concierge {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 0 16px;
          height: 38px;
          background: linear-gradient(135deg, #f1cf85 0%, #d4a857 35%, #b88a3a 70%, #9b6f25 100%);
          color: #1a1208;
          font-size: 11.5px;
          font-weight: 700;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          border: 1px solid rgba(255, 232, 178, 0.6);
          border-radius: 999px;
          text-decoration: none;
          white-space: nowrap;
          overflow: hidden;
          isolation: isolate;
          box-shadow:
            0 1px 0 rgba(255, 245, 215, 0.4) inset,
            0 -1px 0 rgba(80, 50, 10, 0.25) inset,
            0 4px 12px rgba(212, 168, 87, 0.3);
          transition: transform .25s ease, box-shadow .25s ease, filter .25s ease;
        }
        .gpnv-concierge::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(120deg, transparent 20%, rgba(255, 255, 255, 0.55) 48%, transparent 76%);
          transform: translateX(-120%);
          transition: transform .9s ease;
          pointer-events: none;
        }
        .gpnv-concierge:hover {
          color: #1a1208;
          text-decoration: none;
          transform: translateY(-1px);
          filter: brightness(1.04);
          box-shadow:
            0 1px 0 rgba(255, 245, 215, 0.55) inset,
            0 -1px 0 rgba(80, 50, 10, 0.3) inset,
            0 8px 18px rgba(212, 168, 87, 0.42),
            0 0 0 3px rgba(212, 168, 87, 0.15);
        }
        .gpnv-concierge:hover::before { transform: translateX(120%); }
        .gpnv-concierge.is-active {
          background: #1a1208;
          color: #f1cf85;
          border-color: rgba(212, 168, 87, 0.6);
        }
        .gpnv-concierge-short { display: none; }

        /* HAMBURGER */
        .gpnv-burger {
          display: none;
          width: 40px;
          height: 38px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(212, 168, 87, 0.18);
          border-radius: 10px;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          cursor: pointer;
          padding: 0;
          transition: background .2s ease;
        }
        .gpnv-burger:hover { background: rgba(212, 168, 87, 0.18); }
        .gpnv-burger span {
          width: 18px;
          height: 2px;
          background: #d4a857;
          border-radius: 2px;
        }

        /* ============ DRAWER ============ */
        .gpnv-drawer-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          z-index: 1500;
          animation: gpnv-fade .2s ease;
          display: flex;
          justify-content: flex-end;
        }
        .gpnv-drawer {
          width: min(86vw, 380px);
          height: 100vh;
          background: linear-gradient(180deg, #15110a 0%, #0b0b0c 100%);
          border-left: 1px solid rgba(212, 168, 87, 0.25);
          display: flex;
          flex-direction: column;
          padding: 18px 18px 24px;
          gap: 14px;
          overflow-y: auto;
          animation: gpnv-slidein .25s ease;
        }
        @keyframes gpnv-slidein {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .gpnv-drawer::-webkit-scrollbar { width: 6px; }
        .gpnv-drawer::-webkit-scrollbar-thumb { background: rgba(212, 168, 87, 0.25); border-radius: 999px; }

        .gpnv-drawer-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-bottom: 10px;
          border-bottom: 1px solid rgba(212, 168, 87, 0.15);
        }
        .gpnv-drawer-brand img { height: 44px; width: auto; object-fit: contain; }
        .gpnv-drawer-close {
          width: 36px;
          height: 36px;
          background: rgba(212, 168, 87, 0.1);
          border: 1px solid rgba(212, 168, 87, 0.3);
          border-radius: 10px;
          color: #d4a857;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .gpnv-drawer-close:hover { background: rgba(212, 168, 87, 0.2); }

        .gpnv-drawer-search {
          display: flex;
          align-items: center;
          gap: 10px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(212, 168, 87, 0.2);
          color: #9aa0a8;
          padding: 12px 14px;
          border-radius: 10px;
          font-size: 13px;
          cursor: pointer;
          font-family: inherit;
          text-align: left;
          transition: border-color .2s ease, background .2s ease, color .2s ease;
        }
        .gpnv-drawer-search:hover {
          border-color: #d4a857;
          background: rgba(212, 168, 87, 0.08);
          color: #fff;
        }
        .gpnv-drawer-search svg { color: #d4a857; flex-shrink: 0; }

        .gpnv-drawer-section { display: flex; flex-direction: column; gap: 6px; }
        .gpnv-drawer-label {
          font-size: 10px;
          font-weight: 700;
          color: #9aa0a8;
          letter-spacing: 1.8px;
          text-transform: uppercase;
          padding: 0 6px;
          margin-bottom: 4px;
        }
        .gpnv-drawer-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .gpnv-drawer-link {
          width: 100%;
          background: transparent;
          border: none;
          color: #cfd2d6;
          font-size: 14px;
          font-weight: 500;
          padding: 11px 14px;
          border-radius: 9px;
          cursor: pointer;
          font-family: inherit;
          display: flex;
          align-items: center;
          gap: 10px;
          text-align: left;
          transition: background .2s ease, color .2s ease;
        }
        .gpnv-drawer-link svg { color: #d4a857; flex-shrink: 0; }
        .gpnv-drawer-link:hover { background: rgba(212, 168, 87, 0.08); color: #fff; }
        .gpnv-drawer-link.is-active {
          background: linear-gradient(90deg, rgba(212, 168, 87, 0.2), rgba(212, 168, 87, 0.05));
          color: #d4a857;
          font-weight: 600;
        }

        .gpnv-drawer-collapse-head {
          justify-content: space-between !important;
        }
        .gpnv-drawer-sublist {
          list-style: none;
          padding: 4px 0 4px 18px;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
          border-left: 2px solid rgba(212, 168, 87, 0.18);
          margin-left: 14px;
        }
        .gpnv-drawer-sublink {
          width: 100%;
          background: transparent;
          border: none;
          color: #9aa0a8;
          font-size: 13px;
          padding: 8px 12px;
          border-radius: 7px;
          cursor: pointer;
          font-family: inherit;
          display: flex;
          align-items: center;
          gap: 8px;
          text-align: left;
        }
        .gpnv-drawer-sublink:hover { background: rgba(212, 168, 87, 0.08); color: #d4a857; }
        .gpnv-drawer-subicon {
          width: 18px;
          height: 18px;
          border-radius: 4px;
          object-fit: cover;
          background: rgba(255, 255, 255, 0.06);
        }

        .gpnv-drawer-currency {
          display: flex;
          gap: 6px;
        }
        .gpnv-drawer-currbtn {
          flex: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(212, 168, 87, 0.2);
          color: #cfd2d6;
          padding: 10px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
        }
        .gpnv-drawer-currbtn img { width: 18px; height: 12px; border-radius: 2px; object-fit: cover; }
        .gpnv-drawer-currbtn:hover { border-color: #d4a857; color: #fff; }
        .gpnv-drawer-currbtn.is-active {
          background: linear-gradient(135deg, #d4a857, #b58a3f);
          color: #1a1208;
          border-color: transparent;
        }

        .gpnv-drawer-concierge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          color: #1a1208 !important;
          padding: 13px 16px;
          border-radius: 10px;
          font-weight: 700;
          font-size: 12px;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          text-decoration: none;
          margin-top: 4px;
        }
        .gpnv-drawer-concierge:hover {
          color: #1a1208 !important;
          text-decoration: none;
          filter: brightness(1.05);
        }

        .gpnv-drawer-account { margin-top: 4px; }
        .gpnv-drawer-signbtn {
          width: 100%;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 12px 14px;
          background: rgba(212, 168, 87, 0.08);
          border: 1px solid rgba(212, 168, 87, 0.3);
          color: #d4a857;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          font-family: inherit;
          text-align: left;
        }
        .gpnv-drawer-signbtn:hover { background: rgba(212, 168, 87, 0.15); }
        .gpnv-drawer-signbtn small {
          margin-left: auto;
          font-size: 11px;
          color: #9aa0a8;
          font-weight: 400;
          white-space: nowrap;
          max-width: 100px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .gpnv-drawer-contact {
          margin-top: 8px;
          padding-top: 16px;
          border-top: 1px dashed rgba(212, 168, 87, 0.2);
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .gpnv-drawer-contact a {
          color: #9aa0a8;
          font-size: 12.5px;
          text-decoration: none;
        }
        .gpnv-drawer-contact a:hover { color: #d4a857; }

        /* ============ RESPONSIVE BREAKPOINTS ============ */

        /* Hide cart total at smaller widths to keep one row */
        @media (max-width: 1280px) {
          .gpnv-cart-meta, .gpnv-account-meta { display: none; }
          .gpnv-cart, .gpnv-account {
            padding: 0;
            width: 38px;
            height: 38px;
            justify-content: center;
            gap: 0;
          }
        }

        /* Tablet — collapse primary nav and category dropdown */
        @media (max-width: 1100px) {
          .gpnv-links, .gpnv-category { display: none; }
          .gpnv-burger { display: flex; }
          .gpnv-concierge-label { display: none; }
          .gpnv-concierge-short { display: inline; }
          .gpnv-concierge { padding: 0 14px; }
        }

        /* Mobile — hide currency from top bar (drawer has it) */
        @media (max-width: 640px) {
          .gpnv-inner { padding: 0 14px; gap: 8px; }
          .gpnv-curr { display: none; }
          .gpnv-account { display: none; }
          .gpnv-concierge {
            display: none;
          }
          .gpnv-brand img { height: 44px; }
          .gpnv-scrolled .gpnv-brand img { height: 40px; }
          .gpnv-spacer, .gpnv-spacer-scrolled { height: 64px; }
          .gpnv-inner { height: 64px; }
          .gpnv-scrolled .gpnv-inner { height: 60px; }
          .gpnv-spacer-scrolled { height: 60px; }
        }

        @media (max-width: 380px) {
          .gpnv-cart-icon svg { width: 18px; height: 18px; }
          .gpnv-icon-btn, .gpnv-cart, .gpnv-burger { width: 36px; height: 36px; }
        }

        /* ============ AVATAR ============ */
        .gpnv-avatar {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          color: #1a1208;
          font-weight: 800;
          font-family: "Playfair Display", "Times New Roman", serif;
          letter-spacing: 0.5px;
          border-radius: 50%;
          overflow: hidden;
          flex-shrink: 0;
          box-shadow: 0 0 0 2px rgba(212, 168, 87, 0.2);
        }
        .gpnv-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .gpnv-avatar-sm { width: 28px; height: 28px; font-size: 11px; }
        .gpnv-avatar-md { width: 44px; height: 44px; font-size: 15px; box-shadow: 0 0 0 3px rgba(212, 168, 87, 0.18); }
        .gpnv-avatar-lg { width: 54px; height: 54px; font-size: 18px; box-shadow: 0 0 0 3px rgba(212, 168, 87, 0.25); }

        /* ============ ACCOUNT DROPDOWN ============ */
        .gpnv-account-wrap { position: relative; }
        .gpnv-account-chev { margin-left: 2px; color: #d4a857; }

        .gpnv-account-pop {
          position: absolute;
          top: calc(100% + 10px);
          right: 0;
          width: 300px;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.3);
          border-radius: 16px;
          padding: 16px;
          box-shadow: 0 18px 44px rgba(0, 0, 0, 0.6);
          z-index: 1100;
          animation: gpnv-fade .18s ease;
        }
        .gpnv-account-pop-head {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 4px 4px 14px;
          border-bottom: 1px solid rgba(212, 168, 87, 0.18);
          margin-bottom: 12px;
        }
        .gpnv-account-pop-id {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }
        .gpnv-account-pop-id strong {
          font-size: 14px;
          color: #fff;
          font-weight: 700;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .gpnv-account-pop-id small {
          font-size: 12px;
          color: #9aa0a8;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .gpnv-account-pop-stats {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 6px;
          padding: 12px;
          background: rgba(212, 168, 87, 0.06);
          border: 1px dashed rgba(212, 168, 87, 0.22);
          border-radius: 10px;
          margin-bottom: 12px;
        }
        .gpnv-account-stat {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          text-align: center;
          min-width: 0;
        }
        .gpnv-account-stat small {
          font-size: 9.5px;
          color: #9aa0a8;
          text-transform: uppercase;
          letter-spacing: 1.2px;
          font-weight: 700;
        }
        .gpnv-account-stat strong {
          font-size: 13px;
          color: #d4a857;
          font-weight: 800;
          font-family: "Playfair Display", serif;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }

        .gpnv-account-pop-list { display: flex; flex-direction: column; gap: 2px; }
        .gpnv-account-pop-link {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          background: transparent;
          border: 1px solid transparent;
          color: #cfd2d6;
          font-size: 13.5px;
          font-weight: 500;
          border-radius: 10px;
          cursor: pointer;
          font-family: inherit;
          text-align: left;
          transition: background .2s ease, color .2s ease, border-color .2s ease;
        }
        .gpnv-account-pop-link svg { color: #d4a857; flex-shrink: 0; }
        .gpnv-account-pop-link:hover {
          background: rgba(212, 168, 87, 0.1);
          color: #fff;
          border-color: rgba(212, 168, 87, 0.22);
        }
        .gpnv-account-pop-arrow {
          margin-left: auto;
          color: #6e7480;
          font-size: 14px;
          transition: color .2s ease, transform .2s ease;
        }
        .gpnv-account-pop-link:hover .gpnv-account-pop-arrow {
          color: #d4a857;
          transform: translateX(2px);
        }

        .gpnv-account-pop-divider {
          height: 1px;
          background: rgba(212, 168, 87, 0.18);
          margin: 10px 0;
        }
        .gpnv-account-pop-signout {
          width: 100%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px;
          background: rgba(255, 90, 90, 0.08);
          border: 1px solid rgba(255, 90, 90, 0.28);
          color: #ff7a7a;
          border-radius: 10px;
          font-size: 12.5px;
          font-weight: 700;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          cursor: pointer;
          font-family: inherit;
          transition: background .2s ease, border-color .2s ease, color .2s ease;
        }
        .gpnv-account-pop-signout:hover {
          background: rgba(255, 90, 90, 0.18);
          border-color: rgba(255, 90, 90, 0.5);
          color: #ff5c5c;
        }

        /* ============ DRAWER USER ============ */
        .gpnv-drawer-user {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          background: rgba(212, 168, 87, 0.06);
          border: 1px solid rgba(212, 168, 87, 0.22);
          border-radius: 12px;
          margin-bottom: 8px;
        }
        .gpnv-drawer-user-meta {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
          flex: 1;
        }
        .gpnv-drawer-user-meta strong {
          font-size: 14px;
          color: #fff;
          font-weight: 700;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .gpnv-drawer-user-meta small {
          font-size: 12px;
          color: #9aa0a8;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .gpnv-drawer-user-stats {
          margin-top: 4px;
          font-size: 11px;
          color: #d4a857;
          font-weight: 600;
        }
        .gpnv-drawer-signout {
          background: rgba(255, 90, 90, 0.08) !important;
          border-color: rgba(255, 90, 90, 0.28) !important;
          color: #ff7a7a !important;
        }
        .gpnv-drawer-signout:hover {
          background: rgba(255, 90, 90, 0.15) !important;
        }
        .gpnv-drawer-signout svg { color: #ff7a7a !important; }

        /* ============ LOGOUT MODAL ============ */
        .gpnv-logout-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          z-index: 1600;
          animation: gpnv-modal-fade .2s ease;
        }
        @keyframes gpnv-modal-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .gpnv-logout-modal {
          position: relative;
          width: 100%;
          max-width: 420px;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.3);
          border-radius: 20px;
          padding: 36px 28px 28px;
          text-align: center;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
          animation: gpnv-modal-pop .28s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        @keyframes gpnv-modal-pop {
          from { opacity: 0; transform: scale(0.9) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .gpnv-logout-close {
          position: absolute;
          top: 14px;
          right: 14px;
          width: 32px;
          height: 32px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #9aa0a8;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: inherit;
          transition: background .2s ease, color .2s ease, border-color .2s ease;
        }
        .gpnv-logout-close:hover:not(:disabled) {
          background: rgba(212, 168, 87, 0.15);
          border-color: rgba(212, 168, 87, 0.4);
          color: #d4a857;
        }
        .gpnv-logout-close:disabled { opacity: 0.5; cursor: not-allowed; }

        .gpnv-logout-icon {
          width: 76px;
          height: 76px;
          margin: 0 auto 18px;
          background: linear-gradient(135deg, rgba(212, 168, 87, 0.2), rgba(212, 168, 87, 0.05));
          border: 1px solid rgba(212, 168, 87, 0.4);
          color: #d4a857;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .gpnv-logout-title {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 24px;
          color: #fff;
          font-weight: 700;
          margin: 0 0 8px;
          line-height: 1.25;
        }
        .gpnv-logout-title span {
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          font-style: italic;
          font-weight: 600;
        }
        .gpnv-logout-sub {
          font-size: 13.5px;
          color: #9aa0a8;
          line-height: 1.55;
          margin: 0 0 20px;
          padding: 0 6px;
        }
        .gpnv-logout-user {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          background: rgba(212, 168, 87, 0.06);
          border: 1px dashed rgba(212, 168, 87, 0.22);
          border-radius: 12px;
          margin-bottom: 22px;
          text-align: left;
        }
        .gpnv-logout-user-meta {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
          flex: 1;
        }
        .gpnv-logout-user-meta strong {
          font-size: 13.5px;
          color: #fff;
          font-weight: 700;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .gpnv-logout-user-meta small {
          font-size: 12px;
          color: #9aa0a8;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .gpnv-logout-actions {
          display: flex;
          gap: 10px;
        }
        .gpnv-logout-btn {
          flex: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 13px 16px;
          border-radius: 12px;
          font-size: 12.5px;
          font-weight: 800;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          cursor: pointer;
          font-family: inherit;
          border: none;
          min-height: 48px;
          transition: transform .2s ease, box-shadow .25s ease, background .2s ease;
        }
        .gpnv-logout-btn:disabled { opacity: 0.7; cursor: not-allowed; }
        .gpnv-logout-btn-cancel {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(212, 168, 87, 0.3);
          color: #d4a857;
        }
        .gpnv-logout-btn-cancel:hover:not(:disabled) {
          background: rgba(212, 168, 87, 0.12);
          border-color: rgba(212, 168, 87, 0.55);
          color: #f1cf85;
        }
        .gpnv-logout-btn-confirm {
          background: linear-gradient(135deg, #ff6b6b, #c0392b);
          color: #fff;
        }
        .gpnv-logout-btn-confirm:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 14px 28px rgba(192, 57, 43, 0.4);
        }
        .gpnv-logout-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: gpnv-spin 0.7s linear infinite;
        }
        @keyframes gpnv-spin {
          to { transform: rotate(360deg); }
        }

        /* ============ ACCOUNT POP BADGE (next to label) ============ */
        .gpnv-account-pop-badge {
          margin-left: auto;
          margin-right: 8px;
          padding: 2px 8px;
          background: rgba(212, 168, 87, 0.12);
          border: 1px solid rgba(212, 168, 87, 0.3);
          color: #d4a857;
          border-radius: 999px;
          font-size: 9.5px;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
        }
        .gpnv-account-pop-link:has(.gpnv-account-pop-badge) .gpnv-account-pop-arrow {
          margin-left: 0;
        }
        .gpnv-drawer-signbtn small {
          margin-left: auto;
          padding: 2px 8px;
          background: rgba(212, 168, 87, 0.12);
          border: 1px solid rgba(212, 168, 87, 0.3);
          color: #d4a857;
          border-radius: 999px;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.6px;
          text-transform: uppercase;
        }

        /* ============ PASSWORD MODAL ============ */
        .gpnv-pw-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          z-index: 1600;
          animation: gpnv-modal-fade .2s ease;
          overflow-y: auto;
        }
        .gpnv-pw-modal {
          position: relative;
          width: 100%;
          max-width: 460px;
          background: linear-gradient(180deg, #15110a 0%, #1a1208 100%);
          border: 1px solid rgba(212, 168, 87, 0.3);
          border-radius: 20px;
          padding: 32px 28px 26px;
          box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
          animation: gpnv-modal-pop .28s cubic-bezier(0.34, 1.56, 0.64, 1);
          color: #e6e8ec;
          margin: auto;
        }
        .gpnv-pw-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 16px;
          background: linear-gradient(135deg, rgba(212, 168, 87, 0.2), rgba(212, 168, 87, 0.05));
          border: 1px solid rgba(212, 168, 87, 0.4);
          color: #d4a857;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .gpnv-pw-title {
          font-family: "Playfair Display", "Times New Roman", serif;
          font-size: 24px;
          color: #fff;
          font-weight: 700;
          margin: 0 0 8px;
          line-height: 1.25;
          text-align: center;
        }
        .gpnv-pw-title span {
          background: linear-gradient(135deg, #f1cf85, #b58a3f);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          font-style: italic;
          font-weight: 600;
        }
        .gpnv-pw-sub {
          font-size: 13px;
          color: #9aa0a8;
          line-height: 1.55;
          margin: 0 0 18px;
          text-align: center;
        }

        .gpnv-pw-social-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(212, 168, 87, 0.25);
          border-radius: 10px;
          color: #d4a857;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.4px;
          margin: 0 auto 18px;
          width: fit-content;
          max-width: 100%;
        }
        .gpnv-pw-social-email {
          color: #9aa0a8;
          font-weight: 500;
          font-size: 11.5px;
          padding-left: 8px;
          border-left: 1px solid rgba(212, 168, 87, 0.2);
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .gpnv-pw-form { display: flex; flex-direction: column; gap: 14px; }
        .gpnv-pw-field { display: flex; flex-direction: column; gap: 6px; }
        .gpnv-pw-field label {
          font-size: 11px;
          font-weight: 700;
          color: #9aa0a8;
          letter-spacing: 1.2px;
          text-transform: uppercase;
        }
        .gpnv-pw-input-wrap {
          position: relative;
          display: flex;
          align-items: center;
        }
        .gpnv-pw-input-wrap input {
          width: 100%;
          background: rgba(0, 0, 0, 0.35);
          border: 1px solid rgba(212, 168, 87, 0.22);
          border-radius: 10px;
          color: #fff;
          font-size: 14px;
          padding: 12px 44px 12px 14px;
          outline: none;
          font-family: inherit;
          transition: border-color .2s ease, background .2s ease;
        }
        .gpnv-pw-input-wrap input::placeholder { color: #6e7480; }
        .gpnv-pw-input-wrap input:focus { border-color: rgba(212, 168, 87, 0.6); background: rgba(0, 0, 0, 0.5); }
        .gpnv-pw-input-wrap input:disabled { opacity: 0.6; cursor: not-allowed; }
        .gpnv-pw-toggle {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: #9aa0a8;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          border-radius: 6px;
          font-family: inherit;
        }
        .gpnv-pw-toggle:hover { color: #d4a857; background: rgba(212, 168, 87, 0.08); }

        .gpnv-pw-strength {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 4px;
        }
        .gpnv-pw-strength-bar {
          flex: 1;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 3px;
        }
        .gpnv-pw-strength-bar span {
          height: 4px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.08);
          transition: background .25s ease;
        }
        .gpnv-pw-strength-1 span:nth-child(-n+1),
        .gpnv-pw-strength-2 span:nth-child(-n+2),
        .gpnv-pw-strength-3 span:nth-child(-n+3),
        .gpnv-pw-strength-4 span:nth-child(-n+4) { background: linear-gradient(90deg, #d4a857, #b58a3f); }
        .gpnv-pw-strength-1 span:nth-child(-n+1) { background: #ff7a7a; }
        .gpnv-pw-strength-2 span:nth-child(-n+2) { background: #ffb55a; }
        .gpnv-pw-strength-3 span:nth-child(-n+3) { background: #d4a857; }
        .gpnv-pw-strength-4 span:nth-child(-n+4) { background: #4ade80; }
        .gpnv-pw-strength-label {
          font-size: 11px;
          color: #9aa0a8;
          font-weight: 700;
          letter-spacing: 0.5px;
          min-width: 60px;
          text-align: right;
        }

        .gpnv-pw-alert {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          font-size: 12.5px;
          font-weight: 600;
          border-radius: 10px;
          line-height: 1.4;
        }
        .gpnv-pw-alert-error {
          background: rgba(255, 90, 90, 0.1);
          border: 1px solid rgba(255, 90, 90, 0.3);
          color: #ff7a7a;
        }
        .gpnv-pw-alert-success {
          background: rgba(74, 222, 128, 0.1);
          border: 1px solid rgba(74, 222, 128, 0.3);
          color: #4ade80;
        }

        .gpnv-pw-actions {
          display: flex;
          gap: 10px;
          margin-top: 4px;
        }
        .gpnv-pw-submit {
          flex: 1;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 13px 16px;
          border-radius: 12px;
          font-size: 12.5px;
          font-weight: 800;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          cursor: pointer;
          font-family: inherit;
          border: none;
          background: linear-gradient(135deg, #f1cf85, #d4a857 50%, #b58a3f);
          color: #0b0b0c;
          min-height: 48px;
          transition: transform .2s ease, box-shadow .25s ease;
        }
        .gpnv-pw-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 14px 30px rgba(212, 168, 87, 0.35);
        }
        .gpnv-pw-submit:disabled { opacity: 0.7; cursor: not-allowed; }

        @media (max-width: 480px) {
          .gpnv-account-pop { width: calc(100vw - 28px); right: -8px; }
          .gpnv-logout-modal { padding: 28px 22px 22px; }
          .gpnv-logout-actions { flex-direction: column-reverse; }
          .gpnv-pw-modal { padding: 26px 20px 22px; }
          .gpnv-pw-actions { flex-direction: column-reverse; }
          .gpnv-pw-social-email { display: none; }
        }
      `}</style>
    </>
  );
};

const mapStateToProps = (state) => ({
  carts: state?.cartList?.cart,
  w_list: state?.wishList?.w_list,
  configs: state?.configs,
});

export default connect(mapStateToProps, { logout })(Header);
